// ═══════════════════════════════════════════════════════════════════════════
// dealMatching.js — match sponsor deals to podcast hosts, both directions.
//
//   • Host side: "sponsorships matched to you" — rank live/approved deals
//     against a host's niche + episode count (do they qualify yet?).
//   • Sponsor side: "hosts that fit" — rank public hosts against a brand's
//     deals so the sponsor sees who to reach.
//
// Heuristic (no ML), same spirit as matching.js's scoreMatch. Guests never
// enter either direction — deals are a host<->brand thing only.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

const STOP = new Set(['the','and','for','with','that','this','from','about','your','you','our','are','not','a','an','of','to','in','on','is','it','as','at','by','be','or']);
function toks(text = '') {
  return new Set(
    String(text).toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter((w) => w.length > 2 && !STOP.has(w))
  );
}

/** Score a host against a deal. Returns { score 0-100, eligible, reasons[] }.
 *  `eligible` = the host already clears the deal's minimum-episode bar. */
export function scoreDealForHost(deal = {}, host = {}) {
  const reasons = [];
  let score = 0;

  const dealNiches = (deal.niches || []).map((n) => String(n).toLowerCase().trim()).filter(Boolean);
  const hostNiche = String(host.niche || '').toLowerCase().trim();
  const hostText = toks([host.niche, host.bio, host.show_name].filter(Boolean).join(' '));

  // Niche fit (up to 60).
  let matchedNiche = null;
  for (const dn of dealNiches) {
    if (hostNiche && (hostNiche.includes(dn) || dn.includes(hostNiche))) { matchedNiche = dn; break; }
  }
  if (matchedNiche) {
    score += 60;
    reasons.push(`Fits your niche`);
  } else if (dealNiches.length) {
    const dealTokens = new Set(dealNiches.flatMap((n) => n.split(/\s+/)).filter((w) => w.length > 2));
    const overlap = [...dealTokens].filter((t) => hostText.has(t)).length;
    if (overlap > 0) { score += Math.min(38, overlap * 20); reasons.push('Related to your show'); }
  }

  // Episode-minimum eligibility (up to 25).
  const eps = Number(host.episodes_created) || 0;
  const min = Number(deal.min_eps) || 0;
  const eligible = eps >= min;
  if (eligible) {
    score += 25;
    if (min > 0) reasons.push(`You clear the ${min}-episode minimum`);
  } else {
    reasons.push(`${min - eps} more ${min - eps === 1 ? 'episode' : 'episodes'} to qualify`);
  }

  // Small signals: has an audience, recently active (up to 15).
  if ((Number(host.avg_listeners) || 0) > 0) score += 8;
  if (host.updated_at) {
    const days = (Date.now() - new Date(host.updated_at).getTime()) / 86400000;
    if (days < 21) score += 7;
  }

  return { score: Math.max(0, Math.min(100, Math.round(score))), eligible, reasons };
}

/** Rank a brand's live deals for one host. Eligible + higher-scoring first. */
export function rankDealsForHost(deals, host, { limit = 12, minScore = 25 } = {}) {
  const scored = [];
  for (const deal of (deals || [])) {
    const { score, eligible, reasons } = scoreDealForHost(deal, host);
    if (score < minScore) continue;
    scored.push({ deal, score, eligible, reasons });
  }
  scored.sort((a, b) => (Number(b.eligible) - Number(a.eligible)) || (b.score - a.score));
  return scored.slice(0, limit);
}

/** Rank public hosts for one deal (sponsor-side "hosts that fit"). */
export function rankHostsForDeal(deal, hosts, { limit = 8, minScore = 30 } = {}) {
  const scored = [];
  for (const host of (hosts || [])) {
    if (!host?.user_id) continue;
    const { score, eligible, reasons } = scoreDealForHost(deal, host);
    if (score < minScore) continue;
    scored.push({ host, score, eligible, reasons });
  }
  scored.sort((a, b) => (Number(b.eligible) - Number(a.eligible)) || (b.score - a.score));
  return scored.slice(0, limit);
}

/** Rank hosts against a brand's WHOLE deal set (best fit across any deal). */
export function rankHostsForBrand(deals, hosts, { limit = 8 } = {}) {
  if (!(deals || []).length) return [];
  const best = new Map(); // user_id -> { host, score, eligible, dealTitle }
  for (const deal of deals) {
    for (const { host, score, eligible } of rankHostsForDeal(deal, hosts, { limit: 50, minScore: 30 })) {
      const prev = best.get(host.user_id);
      if (!prev || score > prev.score) best.set(host.user_id, { host, score, eligible, dealTitle: deal.title });
    }
  }
  return [...best.values()].sort((a, b) => (Number(b.eligible) - Number(a.eligible)) || (b.score - a.score)).slice(0, limit);
}

/** Public host profiles to match against (sponsor side). Hosts only. */
export async function fetchHostPool({ limit = 100 } = {}) {
  if (!supabase) return [];
  try {
    const { data, error } = await supabase
      .from('podcaster_profiles')
      .select('user_id, full_name, show_name, username, niche, bio, avatar_url, avg_listeners, episodes_created, verified, account_type, updated_at')
      .eq('account_type', 'host')
      .order('updated_at', { ascending: false })
      .limit(limit);
    if (error) { console.error('[dealMatching] fetchHostPool:', error.message); return []; }
    return data || [];
  } catch { return []; }
}

/** Brand identity for a set of deal owners (host-facing display), keyed by
 *  user_id. Fails soft to {} so the deal cards still render without it. */
export async function fetchDealBrands(ownerIds) {
  const ids = [...new Set((ownerIds || []).filter(Boolean))];
  if (!ids.length || !supabase) return {};
  try {
    const { data } = await supabase
      .from('podcaster_profiles')
      .select('user_id, brand_name, full_name, contact_email, avatar_url')
      .in('user_id', ids);
    const map = {};
    for (const p of (data || [])) map[p.user_id] = p;
    return map;
  } catch { return {}; }
}
